import * as React from "react";

/**
 * How much of the window the on-screen keyboard covers, in px (0 when it is down).
 *
 * iOS Safari (and the app's WKWebView) never resize the layout viewport for the
 * keyboard, only the visual one — so a bottom-anchored `position: fixed` drawer stays
 * glued to the bottom of the LAYOUT viewport, i.e. behind the keys, and Safari shoves
 * the page around to expose the field instead. Tracking `visualViewport` gives the
 * drawer the number it needs to sit on top of the keyboard and cap itself to the space
 * left above it, the way the app's native sheets do.
 *
 * Anything smaller than a keyboard is the browser chrome (URL bar) folding, which the
 * drawer must not chase.
 */
const KEYBOARD_MIN_PX = 150;

export function useVirtualKeyboardInset(enabled: boolean): number {
  const [inset, setInset] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) {
      setInset(0);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const covered = Math.round(window.innerHeight - vv.height - vv.offsetTop);
      setInset(covered >= KEYBOARD_MIN_PX ? covered : 0);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    vv.addEventListener("resize", schedule);
    vv.addEventListener("scroll", schedule);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener("resize", schedule);
      vv.removeEventListener("scroll", schedule);
    };
  }, [enabled]);

  // The drawer just shrank around the keyboard: bring the field being typed into back
  // inside it (the browser only did this against the old, taller layout).
  React.useEffect(() => {
    if (inset <= 0) return;
    const frame = requestAnimationFrame(() => {
      const el = document.activeElement;
      if (el instanceof HTMLElement && el.matches("input, textarea, [contenteditable]")) {
        el.scrollIntoView({ block: "nearest" });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [inset]);

  return inset;
}

/** Air kept between a keyboard-lifted drawer's top edge and the top of the window. */
const DRAWER_TOP_GAP_PX = 12;

/** Inline style that seats a bottom drawer on the keyboard; nothing when it is down. */
export function keyboardDrawerStyle(inset: number): React.CSSProperties | undefined {
  if (inset <= 0) return undefined;
  return {
    bottom: inset,
    maxHeight: `calc(100% - ${inset + DRAWER_TOP_GAP_PX}px)`,
    // `bottom` is NOT transitioned: the inset also tracks the visual viewport's scroll
    // (a swipe iOS Safari refuses to hand us, e.g. along its edge-gesture strip), and a
    // 200ms tween there lagged the keyboard and opened a gap the page showed through
    // (BDAY-321). The keyboard's own open/close is animated by iOS itself.
    transition: "max-height 200ms ease-out",
    // Bleed the panel's background from its top edge to well below the keyboard (a shadow
    // paints outside `overflow: hidden`), so whatever frame the inset is behind by shows
    // the sheet, not the page. The spread matters: an offset-only shadow is the panel
    // copied 60vh lower, which starts below the keyboard for any panel shorter than 60vh.
    // It stops one corner radius short of the top so a rounded-t-2xl drawer keeps its corners.
    boxShadow: "0 60vh 0 calc(60vh - 1rem) var(--color-background)",
  };
}
